'use client';

import { useEffect } from 'react';
import Script from 'next/script';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_ID;

export default function GoogleAnalytics() {
  const GA_MEASUREMENT_ID = 'G-9SRV06N84B';

  useEffect(() => {
    if (typeof window !== 'undefined' && GA_TRACKING_ID) {
      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];

      // Configure Google Analytics
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };

      window.gtag('js', new Date());
      window.gtag('config', GA_TRACKING_ID, {
        page_title: document.title,
        page_location: window.location.href,
        anonymize_ip: true, // Privacy-focused
        allow_google_signals: false, // Privacy-focused
        allow_ad_personalization_signals: false, // Privacy-focused
        cookie_flags: 'SameSite=None;Secure',
      });
    }
  }, []);

  if (!GA_TRACKING_ID) {
    return null;
  }

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="lazyOnload"
        data-nscript="lazyOnload"
      />
      <Script
        id="google-analytics"
        strategy="lazyOnload"
        data-nscript="lazyOnload"
      >
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_MEASUREMENT_ID}', {
            page_path: window.location.pathname,
            transport_type: 'beacon',
            anonymize_ip: true,
            send_page_view: true
          });
        `}
      </Script>
    </>
  );
}

// Track custom events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track blog post reads
export const trackBlogRead = (
  postTitle: string,
  category: string,
  readingTime: number
) => {
  if (typeof window !== 'undefined' && window.gtag && GA_TRACKING_ID) {
    window.gtag('event', 'blog_read', {
      event_category: 'Blog',
      event_label: postTitle,
      custom_parameter_1: category,
      custom_parameter_2: readingTime,
    });
  }
};
